import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon, LatLngExpression } from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { userAPI, temporaryLocationsAPI } from '../services/api';
import { User, TemporaryLocation } from '../types';
import FriendProfilePanel from '../components/FriendProfilePanel';
import './FriendProfile.css';

// Fix Leaflet default icon
const defaultIcon = new Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to update map view
const MapUpdater: React.FC<{ center: LatLngExpression; zoom: number }> = ({ center, zoom }) => {
  const map = useMap();

  useEffect(() => {
    map.flyTo(center, zoom, { duration: 1.5 });
  }, [center, zoom, map]);

  return null;
};

const FriendProfile: React.FC = () => {
  const { friendId } = useParams<{ friendId: string }>();
  const navigate = useNavigate();

  const [friend, setFriend] = useState<User | null>(null);
  const [tempLocations, setTempLocations] = useState<TemporaryLocation[]>([]);
  const [loading, setLoading] = useState(true);

  // Map state
  const [mapCenter, setMapCenter] = useState<LatLngExpression>([48.8566, 2.3522]);
  const [mapZoom, setMapZoom] = useState(6);

  useEffect(() => {
    const loadFriendData = async () => {
      if (!friendId) return;

      try {
        setLoading(true);
        const { user } = await userAPI.getUserById(parseInt(friendId));
        setFriend(user);

        // Load friend's temporary locations for map markers
        const { temporaryLocations } = await temporaryLocationsAPI.getFriendTemporaryLocations(parseInt(friendId));
        setTempLocations(temporaryLocations);

        // Set initial map center to friend's address
        if (user.address?.latitude && user.address?.longitude) {
          setMapCenter([user.address.latitude, user.address.longitude]);
          setMapZoom(13);
        }
      } catch (err) {
        console.error('Failed to load friend data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadFriendData();
  }, [friendId]);

  const handleAddressClick = (lat: number, lng: number) => {
    setMapCenter([lat, lng]);
    setMapZoom(15);
  };

  const handleClose = () => {
    navigate('/friends');
  };

  if (!friendId) {
    return null;
  }

  return (
    <div className="friend-profile-container">
      {/* Map Background - Shows only friend's markers */}
      <div className="friend-profile-map-background">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MapUpdater center={mapCenter} zoom={mapZoom} />

          {/* Friend's official address marker */}
          {friend?.address?.latitude && friend?.address?.longitude && (
            <Marker
              position={[friend.address.latitude, friend.address.longitude]}
              icon={defaultIcon}
            >
              <Popup>
                <strong>🏠 Adresse officielle</strong>
                <br />
                {friend.first_name} {friend.last_name}
                <br />
                {friend.address.city}, {friend.address.country}
              </Popup>
            </Marker>
          )}

          {/* Temporary locations markers */}
          {tempLocations.map((location) => (
            <Marker
              key={location.id}
              position={[location.latitude, location.longitude]}
              icon={defaultIcon}
            >
              <Popup>
                <strong>📍 {location.name}</strong>
                <br />
                {location.description && (
                  <>
                    {location.description}
                    <br />
                  </>
                )}
                {new Date(location.start_datetime).toLocaleDateString()} -{' '}
                {new Date(location.end_datetime).toLocaleDateString()}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Friend Profile Panel */}
      {!loading && (
        <FriendProfilePanel
          friendId={parseInt(friendId)}
          onClose={handleClose}
          onAddressClick={handleAddressClick}
        />
      )}
    </div>
  );
};

export default FriendProfile;
